import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  Users, Plus, Search, Edit3, Trash2, Printer, Upload, Download,
  Phone, Mail, MapPin, Briefcase, Calendar, X, FileText, User, Archive
} from 'lucide-react';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  STAFF DIRECTORY — HR list with add/edit/print + per-staff files.
// ═══════════════════════════════════════════════════════════════════

const DEPARTMENTS = ['Clinical', 'Admin', 'Support', 'Housekeeping', 'Other'];
const EMP_TYPES = ['full-time', 'part-time', 'contract', 'consultant', 'intern'];
const STATUSES = [
  { key: 'active',     label: 'Active',      color: '#16a34a' },
  { key: 'on-leave',   label: 'On Leave',    color: '#f59e0b' },
  { key: 'resigned',   label: 'Resigned',    color: '#6b7280' },
  { key: 'terminated', label: 'Terminated',  color: '#dc2626' },
];

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const StaffDirectoryPage = ({ API_BASE, currentUser }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const isAdmin = ['owner','admin','super_admin'].includes(currentUser?.role);
  const showMsg = (t, text) => { setMsg({ type: t, text }); setTimeout(() => setMsg({ type: '', text: '' }), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await axios.get(`${API_BASE}/staff-members`, { params });
      setList(res.data || []);
    } catch { showMsg('error', 'Load failed'); }
    finally { setLoading(false); }
  }, [API_BASE, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let l = list;
    if (deptFilter !== 'all') l = l.filter(s => s.department === deptFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      l = l.filter(s =>
        (s.fullName || '').toLowerCase().includes(q) ||
        (s.designation || '').toLowerCase().includes(q) ||
        (s.phone || '').includes(q) ||
        (s.employeeCode || '').toLowerCase().includes(q)
      );
    }
    return l;
  }, [list, deptFilter, search]);

  const deptCounts = useMemo(() => {
    const m = {};
    list.forEach(s => { m[s.department || 'Other'] = (m[s.department || 'Other'] || 0) + 1; });
    return m;
  }, [list]);

  const handleDelete = async (s) => {
    if (!window.confirm(`"${s.fullName}" archive kar dein?`)) return;
    try { await axios.delete(`${API_BASE}/staff-members/${s._id}`); showMsg('success', 'Archived'); load(); }
    catch { showMsg('error', 'Delete failed'); }
  };

  const printStaff = (s) => {
    const w = window.open('', '_blank', 'width=900,height=700');
    const rows = [
      ['Employee Code', s.employeeCode],
      ['Designation', s.designation],
      ['Department', s.department],
      ['Reporting To', s.reportingTo],
      ['Phone', s.phone],
      ['Alt. Phone', s.altPhone],
      ['Email', s.email],
      ['Address', s.address],
      ['City / State / PIN', [s.city, s.state, s.pincode].filter(Boolean).join(', ')],
      ['Date of Birth', fmt(s.dob)],
      ['Blood Group', s.bloodGroup],
      ['Joining Date', fmt(s.joiningDate)],
      ['Employment Type', s.employmentType],
      ['Shift Timings', s.shiftTimings],
      ['Registration No.', s.registrationNo],
      ['Experience', s.experience],
      ['Emergency Contact', [s.emergencyName, s.emergencyPhone, s.emergencyRelation].filter(Boolean).join(' · ')],
    ].filter(([, v]) => v);

    const rowHtml = rows.map(([k, v]) => `<tr><td style="padding:6px 10px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600;width:35%">${k}</td><td style="padding:6px 10px;border:1px solid #e5e7eb">${v}</td></tr>`).join('');

    w.document.write(`<!doctype html><html><head><title>${s.fullName} — Staff Profile</title>
      <style>body{font-family:'Inter',Arial,sans-serif;max-width:760px;margin:24px auto;padding:0 24px;color:#0f172a}
      h1{color:${theme.primaryDark};border-bottom:2px solid ${theme.primary};padding-bottom:8px;margin:0 0 6px}
      .sub{color:#64748b;margin-bottom:18px;font-size:13px}
      table{border-collapse:collapse;width:100%;font-size:13px;margin-bottom:18px}
      .section{font-weight:700;color:${theme.primaryDark};margin:14px 0 6px;font-size:14px}
      </style></head><body>
      <h1>${s.fullName}</h1>
      <div class="sub">${s.designation || ''} ${s.department ? '· ' + s.department : ''}</div>
      <div class="section">Personal Details</div>
      <table>${rowHtml}</table>
      ${s.qualifications ? `<div class="section">Qualifications</div><div>${s.qualifications}</div>` : ''}
      ${s.notes ? `<div class="section">Notes</div><div>${s.notes}</div>` : ''}
      <script>window.onload=()=>window.print();</script></body></html>`);
    w.document.close();
  };

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: 16, background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`, color: 'white', border: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, color: 'white', fontSize: '1.4rem' }}><Users size={22} style={{ verticalAlign: -4, marginRight: 6 }} /> Staff Directory</h2>
            <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: 13 }}>
              {list.length} members · editable profiles · per-staff document attachments · print-ready
            </p>
          </div>
          {isAdmin && (
            <button onClick={() => { setEditing(null); setShowForm(true); }} style={{ ...btnStyle, marginTop: 0, backgroundColor: 'rgba(255,255,255,0.18)', color: 'white', border: '1px solid rgba(255,255,255,0.4)' }}>
              <Plus size={16} style={{ marginRight: 6 }} /> Add Staff
            </button>
          )}
        </div>
      </div>

      {msg.text && (
        <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 8,
          background: msg.type === 'error' ? '#fee' : '#efe',
          color: msg.type === 'error' ? theme.danger : theme.success,
          border: `1px solid ${msg.type === 'error' ? theme.danger : theme.success}44`,
          fontWeight: 600, fontSize: 13.5,
        }}>{msg.text}</div>
      )}

      {/* Filter bar */}
      <div style={{ ...cardStyle, padding: '10px 14px', marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 200px' }}>
          <Search size={14} color={theme.textMuted} style={{ position: 'absolute', left: 10, top: 10 }} />
          <input placeholder="Search name / phone / designation / code…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ ...inputStyle, padding: '7px 10px 7px 32px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
        </div>
        <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)} style={{ ...inputStyle, padding: '7px 10px', fontSize: 13 }}>
          <option value="all">All Departments</option>
          {DEPARTMENTS.map(d => <option key={d} value={d}>{d} ({deptCounts[d] || 0})</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...inputStyle, padding: '7px 10px', fontSize: 13 }}>
          <option value="all">All Statuses</option>
          {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 60 }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 60 }}>
          <Users size={44} color={theme.textSubtle} style={{ marginBottom: 10, opacity: 0.5 }} />
          <p style={{ color: theme.textMuted, fontWeight: 600 }}>Koi staff member nahi mila</p>
          <p style={{ fontSize: 13, color: theme.textSubtle }}>Upar "Add Staff" click karke naya member add karein</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
          {filtered.map(s => {
            const st = STATUSES.find(x => x.key === s.status) || STATUSES[0];
            return (
              <div key={s._id} style={{ ...cardStyle, padding: 14, borderLeft: `4px solid ${st.color}` }}>
                <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                  {s.photoFile ? (
                    <img src={`${API_BASE}/staff-members/${s._id}/photo`} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', background: theme.borderLight }} />
                  ) : (
                    <div style={{ width: 48, height: 48, borderRadius: 8, background: theme.primary + '20', color: theme.primaryDark, fontWeight: 700, fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {(s.fullName || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14.5, color: theme.navy, lineHeight: 1.3 }}>{s.fullName}</div>
                    <div style={{ fontSize: 12, color: theme.textMuted }}>{s.designation || '—'}</div>
                    {s.employeeCode && <div style={{ fontSize: 10.5, color: theme.textMuted }}>#{s.employeeCode}</div>}
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 700, color: st.color, background: st.color + '18', padding: '2px 7px', borderRadius: 4, alignSelf: 'flex-start' }}>{st.label}</span>
                </div>
                <div style={{ display: 'grid', gap: 3, fontSize: 12, color: theme.textMuted, marginBottom: 10 }}>
                  {s.phone && <div><Phone size={11} style={{ verticalAlign: -1, marginRight: 4 }} />{s.phone}</div>}
                  {s.email && <div><Mail size={11} style={{ verticalAlign: -1, marginRight: 4 }} />{s.email}</div>}
                  {s.department && <div><Briefcase size={11} style={{ verticalAlign: -1, marginRight: 4 }} />{s.department}</div>}
                  {s.joiningDate && <div><Calendar size={11} style={{ verticalAlign: -1, marginRight: 4 }} />Joined {fmt(s.joiningDate)}</div>}
                  {s.attachments?.length > 0 && <div><FileText size={11} style={{ verticalAlign: -1, marginRight: 4 }} />{s.attachments.length} document(s)</div>}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button onClick={() => setViewing(s)} style={actBtn}><User size={12} /> Profile</button>
                  <button onClick={() => printStaff(s)} style={actBtn}><Printer size={12} /> Print</button>
                  {isAdmin && (
                    <>
                      <button onClick={() => { setEditing(s); setShowForm(true); }} style={actBtn}><Edit3 size={12} /> Edit</button>
                      <button onClick={() => handleDelete(s)} style={{ ...actBtn, background: theme.danger + '15', color: theme.danger, borderColor: theme.danger + '44' }}><Archive size={12} /></button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <StaffFormModal API_BASE={API_BASE} staff={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); showMsg('success', 'Saved'); }} />
      )}
      {viewing && (
        <StaffProfileModal API_BASE={API_BASE} staff={viewing} onClose={() => setViewing(null)}
          onChanged={() => { load(); }} isAdmin={isAdmin} />
      )}
    </div>
  );
};

// ── Add/Edit Form ──
const StaffFormModal = ({ API_BASE, staff, onClose, onSaved }) => {
  const [form, setForm] = useState({
    fullName: staff?.fullName || '', employeeCode: staff?.employeeCode || '',
    designation: staff?.designation || '', department: staff?.department || '',
    reportingTo: staff?.reportingTo || '',
    phone: staff?.phone || '', altPhone: staff?.altPhone || '', email: staff?.email || '',
    address: staff?.address || '', city: staff?.city || '', state: staff?.state || '', pincode: staff?.pincode || '',
    qualifications: staff?.qualifications || '', experience: staff?.experience || '', registrationNo: staff?.registrationNo || '',
    joiningDate: staff?.joiningDate ? new Date(staff.joiningDate).toISOString().slice(0,10) : '',
    dob: staff?.dob ? new Date(staff.dob).toISOString().slice(0,10) : '',
    employmentType: staff?.employmentType || 'full-time', shiftTimings: staff?.shiftTimings || '',
    emergencyName: staff?.emergencyName || '', emergencyPhone: staff?.emergencyPhone || '', emergencyRelation: staff?.emergencyRelation || '',
    bloodGroup: staff?.bloodGroup || '', notes: staff?.notes || '', status: staff?.status || 'active',
  });
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
      if (photo) fd.append('photo', photo);
      if (staff?._id) {
        await axios.put(`${API_BASE}/staff-members/${staff._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await axios.post(`${API_BASE}/staff-members`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      onSaved();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };

  return (
    <div style={modalBg} onClick={onClose}>
      <form onSubmit={submit} style={{ ...modalStyle, maxWidth: 680 }} onClick={e => e.stopPropagation()}>
        <div style={modalHead}>
          <h3 style={{ margin: 0, color: theme.navy }}>{staff ? 'Edit Staff' : 'Add Staff'}</h3>
          <button type="button" onClick={onClose} style={iconBtn}><X size={16} /></button>
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Full Name *"><input required value={form.fullName} onChange={e => setForm({ ...form, fullName: e.target.value })} style={inputStyle} /></Field>
            <Field label="Employee Code"><input value={form.employeeCode} onChange={e => setForm({ ...form, employeeCode: e.target.value })} style={inputStyle} placeholder="e.g. SDH-001" /></Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <Field label="Designation"><input value={form.designation} onChange={e => setForm({ ...form, designation: e.target.value })} style={inputStyle} /></Field>
            <Field label="Department">
              <select value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} style={inputStyle}>
                <option value="">—</option>
                {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="Reporting To"><input value={form.reportingTo} onChange={e => setForm({ ...form, reportingTo: e.target.value })} style={inputStyle} /></Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <Field label="Phone"><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} style={inputStyle} /></Field>
            <Field label="Alt Phone"><input value={form.altPhone} onChange={e => setForm({ ...form, altPhone: e.target.value })} style={inputStyle} /></Field>
            <Field label="Email"><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={inputStyle} /></Field>
          </div>
          <Field label="Address"><input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} style={inputStyle} /></Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <Field label="City"><input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} style={inputStyle} /></Field>
            <Field label="State"><input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} style={inputStyle} /></Field>
            <Field label="PIN"><input value={form.pincode} onChange={e => setForm({ ...form, pincode: e.target.value })} style={inputStyle} /></Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <Field label="Date of Birth"><input type="date" value={form.dob} onChange={e => setForm({ ...form, dob: e.target.value })} style={inputStyle} /></Field>
            <Field label="Joining Date"><input type="date" value={form.joiningDate} onChange={e => setForm({ ...form, joiningDate: e.target.value })} style={inputStyle} /></Field>
            <Field label="Blood Group"><input value={form.bloodGroup} onChange={e => setForm({ ...form, bloodGroup: e.target.value })} style={inputStyle} placeholder="e.g. B+" /></Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <Field label="Employment Type">
              <select value={form.employmentType} onChange={e => setForm({ ...form, employmentType: e.target.value })} style={inputStyle}>
                {EMP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Shift Timings"><input value={form.shiftTimings} onChange={e => setForm({ ...form, shiftTimings: e.target.value })} style={inputStyle} placeholder="e.g. 09:00–18:00" /></Field>
            <Field label="Registration No."><input value={form.registrationNo} onChange={e => setForm({ ...form, registrationNo: e.target.value })} style={inputStyle} /></Field>
          </div>
          <Field label="Qualifications"><textarea rows={2} value={form.qualifications} onChange={e => setForm({ ...form, qualifications: e.target.value })} style={{ ...inputStyle, fontFamily: 'inherit' }} placeholder="BDS, MDS, certifications…" /></Field>
          <Field label="Experience"><input value={form.experience} onChange={e => setForm({ ...form, experience: e.target.value })} style={inputStyle} placeholder="e.g. 5 years in ortho" /></Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <Field label="Emergency Name"><input value={form.emergencyName} onChange={e => setForm({ ...form, emergencyName: e.target.value })} style={inputStyle} /></Field>
            <Field label="Emergency Phone"><input value={form.emergencyPhone} onChange={e => setForm({ ...form, emergencyPhone: e.target.value })} style={inputStyle} /></Field>
            <Field label="Relation"><input value={form.emergencyRelation} onChange={e => setForm({ ...form, emergencyRelation: e.target.value })} style={inputStyle} placeholder="Spouse/Parent/…" /></Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Status">
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={inputStyle}>
                {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Photo"><input type="file" accept="image/*" onChange={e => setPhoto(e.target.files[0])} /></Field>
          </div>
          <Field label="Notes"><textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={{ ...inputStyle, fontFamily: 'inherit' }} /></Field>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button type="button" onClick={onClose} style={{ ...btnStyle, marginTop: 0, background: 'white', color: theme.navy, border: `1px solid ${theme.border}` }}>Cancel</button>
          <button type="submit" disabled={saving} style={{ ...btnStyle, marginTop: 0 }}>{saving ? 'Saving…' : (staff ? 'Update' : 'Add Staff')}</button>
        </div>
      </form>
    </div>
  );
};

// ── Profile / Attachments Modal ──
const StaffProfileModal = ({ API_BASE, staff, onClose, onChanged, isAdmin }) => {
  const [s, setS] = useState(staff);
  const [attLabel, setAttLabel] = useState('');
  const [attFile, setAttFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const uploadAtt = async (e) => {
    e.preventDefault();
    if (!attFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', attFile);
      fd.append('label', attLabel || attFile.name);
      const res = await axios.post(`${API_BASE}/staff-members/${s._id}/attachment`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setS(res.data); setAttLabel(''); setAttFile(null); onChanged();
    } catch (e) { alert('Upload failed: ' + (e.response?.data?.error || e.message)); }
    finally { setUploading(false); }
  };

  const removeAtt = async (attId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      const res = await axios.delete(`${API_BASE}/staff-members/${s._id}/attachment/${attId}`);
      setS(res.data); onChanged();
    } catch (e) { alert('Delete failed'); }
  };

  return (
    <div style={modalBg} onClick={onClose}>
      <div style={{ ...modalStyle, maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <div style={modalHead}>
          <h3 style={{ margin: 0, color: theme.navy }}>{s.fullName}</h3>
          <button onClick={onClose} style={iconBtn}><X size={16} /></button>
        </div>
        <div style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
          {s.photoFile ? (
            <img src={`${API_BASE}/staff-members/${s._id}/photo`} alt="" style={{ width: 72, height: 72, borderRadius: 10, objectFit: 'cover' }} />
          ) : (
            <div style={{ width: 72, height: 72, borderRadius: 10, background: theme.primary + '20', color: theme.primaryDark, fontWeight: 700, fontSize: 26, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{(s.fullName || '?').charAt(0).toUpperCase()}</div>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 17, color: theme.navy }}>{s.fullName}</div>
            <div style={{ fontSize: 13, color: theme.textMuted }}>{s.designation || '—'} · {s.department || '—'}</div>
            {s.employeeCode && <div style={{ fontSize: 11.5, color: theme.textMuted }}>#{s.employeeCode}</div>}
          </div>
        </div>

        <div style={{ fontWeight: 700, fontSize: 13, color: theme.primaryDark, marginBottom: 6 }}>📁 Documents ({s.attachments?.length || 0})</div>
        <div style={{ marginBottom: 14 }}>
          {(!s.attachments || s.attachments.length === 0) ? (
            <div style={{ padding: 14, background: theme.bg, borderRadius: 8, textAlign: 'center', color: theme.textMuted, fontSize: 13 }}>No files attached yet</div>
          ) : s.attachments.map(a => (
            <div key={a._id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: `1px solid ${theme.border}`, borderRadius: 8, marginBottom: 6 }}>
              <FileText size={16} color={theme.primary} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: theme.navy }}>{a.label}</div>
                <div style={{ fontSize: 11, color: theme.textMuted }}>{a.originalName} · {(a.fileSize / 1024).toFixed(1)} KB</div>
              </div>
              <a href={`${API_BASE}/staff-members/${s._id}/file/${a._id}`} style={actBtn}><Download size={12} /> Open</a>
              {isAdmin && <button onClick={() => removeAtt(a._id)} style={{ ...actBtn, background: theme.danger + '15', color: theme.danger, borderColor: theme.danger + '44' }}><Trash2 size={12} /></button>}
            </div>
          ))}
        </div>

        {isAdmin && (
          <form onSubmit={uploadAtt} style={{ display: 'flex', gap: 6, padding: 10, background: theme.bg, borderRadius: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input placeholder="Label (e.g. Aadhaar, BDS Degree)" value={attLabel} onChange={e => setAttLabel(e.target.value)} style={{ ...inputStyle, padding: '6px 10px', fontSize: 12.5, flex: 1, minWidth: 160 }} />
            <input type="file" onChange={e => setAttFile(e.target.files[0])} style={{ fontSize: 12 }} />
            <button type="submit" disabled={uploading || !attFile} style={{ ...actBtn, background: theme.primary, color: 'white', borderColor: theme.primary }}>
              <Upload size={12} /> {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label style={lbl}>{label}</label>
    {children}
  </div>
);

const actBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '5px 10px', borderRadius: 6,
  border: `1px solid ${theme.primary}33`,
  background: theme.primary + '12', color: theme.primary,
  cursor: 'pointer', fontSize: 11.5, fontWeight: 600, textDecoration: 'none',
};
const iconBtn = { padding: 4, border: 'none', background: 'transparent', cursor: 'pointer', color: theme.textMuted };
const modalBg = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const modalStyle = { background: 'white', borderRadius: 12, padding: 20, width: '100%', maxWidth: 520, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', maxHeight: '92vh', overflowY: 'auto' };
const modalHead = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 };
const lbl = { display: 'block', fontSize: 11, fontWeight: 700, color: theme.textMuted, marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.4 };

export default StaffDirectoryPage;
